import React, { useState, useEffect } from 'react';

const BOT_URL = process.env.REACT_APP_BOT_URL || 'http://localhost:8080';

export default function WhatsAppManager() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [disconnecting, setDisconnecting] = useState(false);
  const [error, setError] = useState('');
  
  // Custom Modal State
  const [modal, setModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'confirm', // 'confirm' or 'alert'
    onConfirm: null
  });

  const adminPassword = sessionStorage.getItem('eco_admin_pw') || '';

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${BOT_URL}/api/whatsapp/status?adminPassword=${encodeURIComponent(adminPassword)}`);
      if (res.status === 401) {
        setError('Unauthorized access. Please re-login.');
        return;
      }
      const data = await res.json();
      if (data.success) {
        setStatus(data);
        setError('');
      } else {
        setError(data.error || 'Failed to fetch status');
      }
    } catch (err) {
      console.error(err);
      setError('Failed to communicate with the bot server.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    // Poll status every 5 seconds to get updated QR and connection state
    const interval = setInterval(fetchStatus, 5000);
    return () => clearInterval(interval);
  }, []);

  const triggerDisconnect = async () => {
    setDisconnecting(true);
    setModal(prev => ({ ...prev, isOpen: false }));
    try {
      const res = await fetch(`${BOT_URL}/api/whatsapp/logout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ adminPassword })
      });
      const data = await res.json();
      if (res.status === 200 && data.success) {
        setStatus(prev => ({ ...prev, connected: false, qr: null }));
        fetchStatus();
      } else {
        setModal({
          isOpen: true,
          title: '❌ Failed to Disconnect',
          message: data.error || 'Failed to disconnect',
          type: 'alert',
          onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
        });
      }
    } catch (err) {
      setModal({
        isOpen: true,
        title: '❌ Server Error',
        message: 'Failed to disconnect bot server. Please check connection.',
        type: 'alert',
        onConfirm: () => setModal(prev => ({ ...prev, isOpen: false }))
      });
    } finally {
      setDisconnecting(false);
    }
  };

  const handleDisconnect = () => {
    setModal({
      isOpen: true,
      title: '⚠️ Disconnect WhatsApp?',
      message: 'Are you sure you want to disconnect this WhatsApp number? This will stop the bot until a new number is scanned.',
      type: 'confirm',
      onConfirm: triggerDisconnect
    });
  };

  if (loading && !status) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '300px', color: '#94a3b8' }}>
        <div style={{
          border: '4px solid rgba(255, 255, 255, 0.1)',
          borderLeftColor: '#10b981',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          animation: 'spin 1s linear infinite',
          marginBottom: '16px'
        }}></div>
        <p>Fetching WhatsApp Bot Status...</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: '600px', margin: '0 auto' }}>
      <div style={{
        background: 'rgba(30, 41, 59, 0.7)',
        backdropFilter: 'blur(12px)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        borderRadius: '16px',
        padding: '32px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '700', color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '8px' }}>
            💬 WhatsApp Bot Settings
          </h2>
          {status?.connected ? (
            <span style={{
              fontSize: '11px',
              fontWeight: '700',
              background: 'rgba(16, 185, 129, 0.15)',
              color: '#34d399',
              padding: '4px 12px',
              borderRadius: '20px',
              border: '1px solid rgba(16, 185, 129, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Connected ✅
            </span>
          ) : (
            <span style={{
              fontSize: '11px',
              fontWeight: '700',
              background: 'rgba(239, 68, 68, 0.15)',
              color: '#f87171',
              padding: '4px 12px',
              borderRadius: '20px',
              border: '1px solid rgba(239, 68, 68, 0.3)',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Disconnected ❌
            </span>
          )}
        </div>

        {error && (
          <div style={{
            background: 'rgba(239, 68, 68, 0.1)',
            border: '1px solid rgba(239, 68, 68, 0.2)',
            borderRadius: '8px',
            padding: '12px',
            color: '#f87171',
            fontSize: '14px',
            marginBottom: '20px'
          }}>
            {error}
          </div>
        )}

        {status?.connected ? (
          <div>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              Bot is currently online and active. It is listening to group attendance messages.
            </p>
            <div style={{
              background: 'rgba(15, 23, 42, 0.4)',
              borderRadius: '12px',
              padding: '20px',
              border: '1px solid rgba(255, 255, 255, 0.04)',
              marginBottom: '32px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span style={{ color: '#64748b', fontSize: '13px' }}>Linked Phone</span>
                <span style={{ color: '#fbbf24', fontWeight: '700', fontSize: '15px' }}>+{status.phone}</span>
              </div>
              {status.name && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#64748b', fontSize: '13px' }}>WhatsApp Profile</span>
                  <span style={{ color: '#f8fafc', fontWeight: '600', fontSize: '14px' }}>{status.name}</span>
                </div>
              )}
            </div>

            <button
              onClick={handleDisconnect}
              disabled={disconnecting}
              style={{
                width: '100%',
                padding: '14px',
                background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                fontWeight: '600',
                fontSize: '14px',
                cursor: disconnecting ? 'default' : 'pointer',
                transition: 'opacity 0.2s',
                opacity: disconnecting ? 0.6 : 1
              }}
            >
              {disconnecting ? '⏳ Disconnecting...' : '↩ Disconnect WhatsApp Number'}
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', marginBottom: '24px' }}>
              Bot is not linked to any WhatsApp account. Scan the QR code below using your phone's WhatsApp to link it.
            </p>

            {status?.qr ? (
              <div style={{
                background: '#fff',
                padding: '16px',
                borderRadius: '12px',
                display: 'inline-block',
                border: '1px solid #ddd',
                boxShadow: '0 4px 10px rgba(0,0,0,0.15)',
                marginBottom: '20px'
              }}>
                <img
                  src={`https://api.qrserver.com/v1/create-qr-code/?data=${encodeURIComponent(status.qr)}&size=240x240`}
                  alt="WhatsApp QR Code"
                  style={{ display: 'block' }}
                />
              </div>
            ) : (
              <div style={{
                height: '240px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(15, 23, 42, 0.4)',
                borderRadius: '12px',
                border: '1px dashed rgba(255,255,255,0.1)',
                color: '#64748b',
                marginBottom: '20px'
              }}>
                Generating fresh QR code...
              </div>
            )}

            <p style={{ color: '#64748b', fontSize: '12px', fontStyle: 'italic', margin: 0 }}>
              💡 Scan using: <b>WhatsApp > Settings > Linked Devices > Link a Device</b>.
              <br/>
              This panel will automatically refresh and connect once scanned.
            </p>
          </div>
        )}
      </div>

      {/* Custom Popup Modal */}
      {modal.isOpen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(15, 23, 42, 0.75)',
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          padding: '20px',
          boxSizing: 'border-box'
        }}>
          <div style={{
            background: 'rgba(30, 41, 59, 0.95)',
            backdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            padding: '28px',
            width: '100%',
            maxWidth: '400px',
            textAlign: 'center',
            boxShadow: '0 20px 50px rgba(0,0,0,0.4)',
            color: '#f8fafc'
          }}>
            <h3 style={{ margin: '0 0 12px 0', fontSize: '18px', fontWeight: '700', color: '#f8fafc' }}>
              {modal.title}
            </h3>
            <p style={{ color: '#94a3b8', fontSize: '14px', lineHeight: '1.6', margin: '0 0 24px 0' }}>
              {modal.message}
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
              {modal.type === 'confirm' && (
                <button
                  onClick={() => setModal(prev => ({ ...prev, isOpen: false }))}
                  style={{
                    padding: '10px 20px',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '8px',
                    color: '#94a3b8',
                    fontWeight: '600',
                    fontSize: '14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                  onMouseEnter={(e) => { e.target.style.background = 'rgba(255,255,255,0.1)'; e.target.style.color = '#fff'; }}
                  onMouseLeave={(e) => { e.target.style.background = 'rgba(255,255,255,0.05)'; e.target.style.color = '#94a3b8'; }}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={modal.onConfirm}
                style={{
                  padding: '10px 20px',
                  background: modal.type === 'confirm' ? 'linear-gradient(135deg, #ef4444, #dc2626)' : 'linear-gradient(135deg, #10b981, #059669)',
                  border: 'none',
                  borderRadius: '8px',
                  color: '#fff',
                  fontWeight: '600',
                  fontSize: '14px',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                }}
              >
                {modal.type === 'confirm' ? 'Disconnect' : 'OK'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
